// lib/audio-queue.ts

const MAX_IN_FLIGHT = 3;
const IDLE_RESOLVE_DELAY_MS = 0;

export class AudioQueue {
  private queue: Blob[] = [];
  private pending: Promise<void> = Promise.resolve();
  private inFlightFetches = 0;
  private currentAudio: HTMLAudioElement | null = null;
  private currentUrl: string | null = null;
  private stopped = false;
  private resolveEmpty: (() => void) | null = null;
  private emptyPromise: Promise<void> | null = null;
  private audioContext: AudioContext | null = null;
  private onAnalyserChange: ((analyser: AnalyserNode | null) => void) | null = null;

  setAudioContext(
    context: AudioContext,
    onAnalyserChange: (analyser: AnalyserNode | null) => void,
  ): void {
    this.audioContext = context;
    this.onAnalyserChange = onAnalyserChange;
  }

  /**
   * Encola un blob MP3. La reproducción se encadena automáticamente.
   * No reproduce más de MAX_IN_FLIGHT fetches en vuelo (no aplica aquí
   * porque push recibe blobs ya descargados; el límite documentado se
   * respeta a nivel de /api/speak en page.tsx).
   */
  push(blob: Blob): void {
    if (this.stopped) return;
    this.queue.push(blob);
    this.pending = this.pending.then(() => this.playNext());
  }

  /**
   * Reproduce el siguiente blob. Encadena con `onended` para evitar
   * pausas perceptibles entre frases.
   */
  private async playNext(): Promise<void> {
    if (this.stopped) return;
    const blob = this.queue.shift();
    if (!blob) {
      this.checkEmpty();
      return;
    }

    const url = URL.createObjectURL(blob);
    const audio = new Audio(url);
    this.currentAudio = audio;
    this.currentUrl = url;

    let analyser: AnalyserNode | null = null;
    if (this.audioContext) {
      const source = this.audioContext.createMediaElementSource(audio);
      analyser = this.audioContext.createAnalyser();
      analyser.fftSize = 512;
      analyser.smoothingTimeConstant = 0.25;
      source.connect(analyser);
      analyser.connect(this.audioContext.destination);
      this.onAnalyserChange?.(analyser);
    }

    await new Promise<void>((resolve) => {
      const cleanup = () => {
        if (analyser) this.onAnalyserChange?.(null);
        URL.revokeObjectURL(url);
        if (this.currentAudio === audio) {
          this.currentAudio = null;
          this.currentUrl = null;
        }
        resolve();
      };
      audio.onended = cleanup;
      audio.onerror = cleanup;
      audio.play().catch(cleanup);
    });

    this.checkEmpty();
  }

  /**
   * Para el audio actual, vacía la cola, libera URLs. No se puede reanudar.
   */
  stop(): void {
    this.stopped = true;
    this.queue.length = 0;
    if (this.currentAudio) {
      this.onAnalyserChange?.(null);
      this.currentAudio.pause();
      this.currentAudio.src = "";
      this.currentAudio = null;
    }
    if (this.currentUrl) {
      URL.revokeObjectURL(this.currentUrl);
      this.currentUrl = null;
    }
    this.checkEmpty();
  }

  /**
   * Reactiva la cola tras un stop (para nuevas respuestas).
   */
  reset(): void {
    this.stopped = false;
  }

  /**
   * True si no hay blobs encolados ni reproduciéndose.
   */
  isEmpty(): boolean {
    return this.queue.length === 0 && this.currentAudio === null;
  }

  private checkEmpty(): void {
    if (this.isEmpty() && this.resolveEmpty) {
      const r = this.resolveEmpty;
      this.resolveEmpty = null;
      this.emptyPromise = null;
      // Da un tick para que el caller pueda re-comprobar
      setTimeout(r, IDLE_RESOLVE_DELAY_MS);
    }
  }

  /**
   * Promise que se resuelve cuando la cola queda vacía.
   * Útil para transicionar de "speaking" a followup.
   */
  whenEmpty(): Promise<void> {
    if (this.isEmpty()) return Promise.resolve();
    if (!this.emptyPromise) {
      this.emptyPromise = new Promise<void>((resolve) => {
        this.resolveEmpty = resolve;
      });
    }
    return this.emptyPromise;
  }
}

export { MAX_IN_FLIGHT };
